import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { countryService } from '@/services/countryService';
import { CreateCountryRequest } from '@/types/model';

export const countryKeys = {
  all: ['countries'] as const,
  list: () => [...countryKeys.all, 'list'] as const,
  dropdown: () => [...countryKeys.all, 'dropdown'] as const,
  details: () => [...countryKeys.all, 'detail'] as const,
  detail: (id: string) => [...countryKeys.details(), id] as const,
};

export const useCountries = () => {
  return useQuery({
    queryKey: countryKeys.list(),
    queryFn: () => countryService.getCountries(),
  });
};

export const useCountriesList = () => {
  return useQuery({
    queryKey: countryKeys.dropdown(),
    queryFn: () => countryService.getCountries(),
    staleTime: 30 * 60 * 1000,
    select: (countries) =>
      [...countries].sort((a, b) => {
        const orderA = typeof a.displayOrder === 'number' ? a.displayOrder : Number.MAX_SAFE_INTEGER;
        const orderB = typeof b.displayOrder === 'number' ? b.displayOrder : Number.MAX_SAFE_INTEGER;
        return orderA - orderB || a.nameEnglish.localeCompare(b.nameEnglish);
      }),
  });
};

export const useCountry = (id: string) => {
  return useQuery({
    queryKey: countryKeys.detail(id),
    queryFn: () => countryService.getCountryById(id),
    enabled: !!id,
  });
};

export const useCreateCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (countryData: CreateCountryRequest) =>
      countryService.createCountry(countryData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: countryKeys.list() });
      queryClient.invalidateQueries({ queryKey: countryKeys.dropdown() });
    },
  });
};

export const useUpdateCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateCountryRequest> }) =>
      countryService.updateCountry(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: countryKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: countryKeys.list() });
      queryClient.invalidateQueries({ queryKey: countryKeys.dropdown() });
    },
  });
};

export const useDeleteCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => countryService.deleteCountry(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: countryKeys.list() });
      queryClient.invalidateQueries({ queryKey: countryKeys.dropdown() });
      queryClient.invalidateQueries({ queryKey: countryKeys.detail(id) });
    },
  });
};
